import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { UserAvatar } from "@/components/Navbar";
import { ThumbsUp, ThumbsDown, Heart, Angry, HandHeart, Flame, Reply, Send, MoreHorizontal, Flag, Swords, Handshake, EyeOff, Trash2 } from "lucide-react";

export const REACTIONS = [
  { key: "like", Icon: ThumbsUp, label: "Like", color: "#2563EB" },
  { key: "love", Icon: Heart, label: "Love", color: "#E11D48" },
  { key: "care", Icon: HandHeart, label: "Care", color: "#F59E0B" },
  { key: "fire", Icon: Flame, label: "Fire", color: "#EA580C" },
  { key: "hundred", glyph: "100", label: "100", color: "#DC2626" },
  { key: "dislike", Icon: ThumbsDown, label: "Dislike", color: "#64748B" },
  { key: "hate", Icon: Angry, label: "Hate", color: "#7C3AED" },
];
const BY_KEY = Object.fromEntries(REACTIONS.map((r) => [r.key, r]));
// Positive reactions lift a comment; negatives sink it (Top Take ranking).
export const POSITIVE = ["like", "love", "care", "fire", "hundred"];
export const NEGATIVE = ["dislike", "hate"];

const REPORT_REASONS = ["Spam or scam", "Harassment or bullying", "Hate speech", "Misinformation / false claim", "Off-topic or other"];

export function withMyReaction(comment, userId) {
  const reactions = comment.reactions || {};
  const my = userId ? REACTIONS.find((r) => (reactions[r.key] || []).includes(userId))?.key || null : null;
  const counts = comment.reaction_counts || REACTIONS.reduce((a, r) => ({ ...a, [r.key]: (reactions[r.key] || []).length }), {});
  return { ...comment, my_reaction: my, reaction_counts: counts };
}

export function netScore(comment) {
  const c = comment.reaction_counts || {};
  return POSITIVE.reduce((s, k) => s + (c[k] || 0), 0) - NEGATIVE.reduce((s, k) => s + (c[k] || 0), 0);
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function ReactionIcon({ r, size, color }) {
  const style = color ? { color } : undefined;
  if (r.glyph) {
    return <span style={{ ...style, fontSize: Math.round(size * 0.74), fontWeight: 800, lineHeight: 1, letterSpacing: "-0.06em" }}>{r.glyph}</span>;
  }
  const I = r.Icon;
  return <I style={{ ...style, width: size, height: size }} fill="currentColor" />;
}

function ReactionBar({ comment, user, onReact }) {
  const [open, setOpen] = useState(false);
  const counts = comment.reaction_counts || {};
  const total = REACTIONS.reduce((s, r) => s + (counts[r.key] || 0), 0);
  const active = comment.my_reaction ? BY_KEY[comment.my_reaction] : null;
  const summary = REACTIONS.filter((r) => (counts[r.key] || 0) > 0)
    .sort((a, b) => (counts[b.key] || 0) - (counts[a.key] || 0))
    .slice(0, 3);

  const pick = (key) => { setOpen(false); onReact(comment.comment_id, key); };
  const handleMain = () => { if (active) onReact(comment.comment_id, active.key); else setOpen((o) => !o); };

  return (
    <div className="relative flex items-center gap-2" onMouseLeave={() => setOpen(false)}>
      <button
        onClick={handleMain}
        onMouseEnter={() => setOpen(true)}
        className="flex items-center gap-1 text-xs font-semibold transition-colors"
        style={{ color: active ? active.color : undefined }}
        data-testid={`react-trigger-${comment.comment_id}`}
      >
        {active ? <ReactionIcon r={active} size={15} color={active.color} /> : <ThumbsUp className="w-3.5 h-3.5" />}
        <span className={active ? "" : "text-neutral"}>{active ? active.label : "React"}</span>
      </button>

      {total > 0 && (
        <span className="flex items-center gap-0.5 text-[11px] text-neutral" data-testid={`react-summary-${comment.comment_id}`}>
          <span className="flex -space-x-1">
            {summary.map((r) => (
              <span key={r.key} className="w-4 h-4 rounded-full bg-white ring-1 ring-gray-100 flex items-center justify-center" style={{ color: r.color }}>
                <ReactionIcon r={r} size={11} color={r.color} />
              </span>
            ))}
          </span>
          {total}
        </span>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
            className="absolute bottom-full left-0 mb-2 flex items-center gap-1 px-2 py-1.5 bg-white rounded-full shadow-xl border border-gray-100 z-30"
            onMouseEnter={() => setOpen(true)}
            data-testid={`react-tray-${comment.comment_id}`}
          >
            {REACTIONS.map((r) => {
              const mine = comment.my_reaction === r.key;
              return (
                <motion.button
                  key={r.key}
                  whileHover={{ scale: 1.35, y: -4 }}
                  whileTap={{ scale: 1.1 }}
                  onClick={() => pick(r.key)}
                  title={r.label}
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                  style={{ backgroundColor: mine ? `${r.color}1a` : "transparent" }}
                  data-testid={`react-${r.key}-${comment.comment_id}`}
                >
                  <ReactionIcon r={r} size={18} color={r.color} />
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MoreMenu({ comment, user, onReport, onDelete }) {
  const [open, setOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const canReport = user && comment.user?.user_id !== user.user_id;
  if (!canReport && !onDelete) return null; // nothing to show

  return (
    <div className="relative ml-auto" onMouseLeave={() => { setOpen(false); setReporting(false); }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="p-1 text-neutral/60 hover:text-neutral rounded-full hover:bg-gray-100 transition-colors"
        data-testid={`more-${comment.comment_id}`}
        aria-label="More options"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -4 }}
            className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-xl border border-gray-100 p-1.5 z-30"
            data-testid={`more-menu-${comment.comment_id}`}
          >
            {reporting ? (
              <div data-testid={`report-reasons-${comment.comment_id}`}>
                <p className="px-2 py-1.5 text-[11px] font-semibold text-neutral uppercase tracking-wider">Why report?</p>
                {REPORT_REASONS.map((r) => (
                  <button
                    key={r}
                    onClick={() => { onReport(comment.comment_id, r); setOpen(false); setReporting(false); }}
                    className="w-full text-left px-3 py-1.5 text-sm text-dark/80 hover:bg-gray-50 rounded-lg transition-colors"
                    data-testid={`report-reason-${comment.comment_id}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            ) : (
              <>
                {canReport && (
                  <button
                    onClick={() => setReporting(true)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    data-testid={`report-open-${comment.comment_id}`}
                  >
                    <Flag className="w-4 h-4" /> Report comment
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => { onDelete(comment.comment_id); setOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-700 font-semibold hover:bg-red-50 rounded-lg transition-colors"
                    data-testid={`delete-comment-${comment.comment_id}`}
                  >
                    <Trash2 className="w-4 h-4" /> Delete (moderator)
                  </button>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function RelationBadge({ side, parentSide }) {
  if (!side || !parentSide) return null;
  const counter = side !== parentSide;
  return counter ? (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700" data-testid="relation-counterpoint">
      <Swords className="w-3 h-3" /> COUNTERPOINT
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700" data-testid="relation-ally">
      <Handshake className="w-3 h-3" /> ALLY
    </span>
  );
}

function CommentBody({ comment, user, isReply, parentSide, onReact, onReply, onReport, onDelete, sideLabelA, sideLabelB, children }) {
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [replyText, setReplyText] = useState(isReply ? `@${comment.user?.name || ""} ` : "");
  const [submitting, setSubmitting] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const sideColor = comment.voted_side === "a" ? "text-view-a" : comment.voted_side === "b" ? "text-view-b" : "text-neutral";
  const flaggedHidden = comment.auto_flagged && !revealed;

  const submitReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    setSubmitting(true);
    await onReply(comment, replyText);
    setReplyText(isReply ? `@${comment.user?.name || ""} ` : "");
    setShowReplyBox(false);
    setSubmitting(false);
  };

  return (
    <div className="flex gap-3">
      <Link to={`/profile/${comment.user?.user_id}`}>
        <UserAvatar user={comment.user} size={isReply ? "w-7 h-7" : "w-8 h-8"} textSize="text-[10px]" />
      </Link>
      <div className="flex-1 min-w-0">
        {flaggedHidden ? (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid={`flagged-notice-${comment.comment_id}`}>
            <EyeOff className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="text-xs text-amber-800 flex-1">This comment was flagged by the community and is pending review.</span>
            <button
              onClick={() => setRevealed(true)}
              className="text-xs font-semibold text-amber-700 hover:text-amber-900 underline shrink-0"
              data-testid={`flagged-reveal-${comment.comment_id}`}
            >
              Show anyway
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <span className="text-sm font-semibold text-dark">{comment.user?.name || "Anonymous"}</span>
              {comment.voted_side && (
                <span className={`text-[10px] font-bold uppercase tracking-wider ${sideColor}`}>{comment.voted_side === "a" ? (sideLabelA || "View A") : (sideLabelB || "View B")}</span>
              )}
              {isReply && <RelationBadge side={comment.voted_side} parentSide={parentSide} />}
              {comment.auto_flagged && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"><Flag className="w-2.5 h-2.5" /> FLAGGED</span>}
              <span className="text-[11px] text-neutral">{timeAgo(comment.created_at)}</span>
              <MoreMenu comment={comment} user={user} onReport={onReport} onDelete={onDelete} />
            </div>
            <p className="text-sm text-dark/80 leading-relaxed whitespace-pre-wrap break-words">{comment.text}</p>

            <div className="flex items-center gap-4 mt-1.5">
              <ReactionBar comment={comment} user={user} onReact={onReact} />
              <button
                onClick={() => setShowReplyBox((s) => !s)}
                className="flex items-center gap-1 text-xs text-neutral hover:text-view-a transition-colors font-semibold"
                data-testid={`reply-btn-${comment.comment_id}`}
              >
                <Reply className="w-3.5 h-3.5" /> Reply
              </button>
            </div>

            {showReplyBox && user && (
              <form onSubmit={submitReply} className="flex gap-2 mt-2" data-testid={`reply-form-${comment.comment_id}`}>
                <input
                  autoFocus
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={`Reply to ${comment.user?.name || "comment"}...`}
                  className="flex-1 text-sm px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-view-a/30 focus:border-view-a transition-all"
                  data-testid={`reply-input-${comment.comment_id}`}
                />
                <button type="submit" disabled={!replyText.trim() || submitting} className="p-2 bg-view-a text-white rounded-lg hover:bg-view-a-dark transition-colors disabled:opacity-40" data-testid={`reply-submit-${comment.comment_id}`}>
                  <Send className="w-3.5 h-3.5" />
                </button>
              </form>
            )}
            {showReplyBox && !user && <p className="text-xs text-neutral mt-2">Sign in to reply.</p>}
          </>
        )}

        {children}
      </div>
    </div>
  );
}

export function CommentItem({ comment, replies, user, onReact, onReply, onReport, onDelete, sideLabelA, sideLabelB }) {
  return (
    <div className="py-3" data-testid={`comment-${comment.comment_id}`}>
      <CommentBody comment={comment} user={user} isReply={false} onReact={onReact} onReply={onReply} onReport={onReport} onDelete={onDelete} sideLabelA={sideLabelA} sideLabelB={sideLabelB}>
        {replies?.length > 0 && (
          <div className="mt-3 space-y-3 pl-3 border-l-2 border-gray-100">
            {replies.map((r) => (
              <div key={r.comment_id} data-testid={`comment-${r.comment_id}`}>
                <CommentBody comment={r} user={user} isReply parentSide={comment.voted_side} onReact={onReact} onReply={onReply} onReport={onReport} onDelete={onDelete} sideLabelA={sideLabelA} sideLabelB={sideLabelB} />
              </div>
            ))}
          </div>
        )}
      </CommentBody>
    </div>
  );
}
